<template>
  <CanvasContent v-if="displayMode === 'print'" class="canvas-builder-app canvas-builder-app--print" />
  <CanvasContent
    v-else-if="mode === 'home'"
    class="canvas-builder-app h-full"
    :class="{ 'canvas-builder-app--home': true }"
  />
  <div v-else class="canvas-builder-app h-full" :class="{ [`canvas-builder-app--${mode}`]: true }">
    <NProgress
      v-if="hasProgressBar"
      :border-radius="0"
      class="canvas-builder--progress-bar top-0 right-0 pb-1 absolute"
      :color="themeVars.primary"
      :fill-border-radius="0"
      :height="4"
      :percentage="presentationProgressPercentage"
      :rail-color="themeBgColor"
      :show-indicator="false"
      type="line"
    />

    <div
      class="h-full flex flex-auto overflow-auto"
      :class="{ 'pt-2': hasProgressBar && !isFullscreen, 'pt-1 overflow-hidden': hasProgressBar && isFullscreen }"
    >
      <div
        v-if="shouldShowCanvasName"
        class="fixed flex items-center align-center gap-2 z-20 top-2 color-primary2 left-1/2 -translate-x-1/2 px-2 text-sm font-semibold tracking-wide backdrop-blur-md pa-1 rounded-md"
      >
        <i class="c-icon far fa-presentation n-icon" role="img" style="--n-bezier: 0; font-size: 14px" />
        <span class="drop-shadow-sm">{{ activeCanvas?.name }}</span>
      </div>

      <Navbar v-show="shouldShowSidebars" />
      <CanvasContent />
      <ControlBar
        v-if="shouldShowSidebars"
        :hide-edit="
          props.hideEdit ||
          (props.mode === CanvasBuilderMode.IMPACT &&
            launchDarkly?.disable_canvas_edit_for_reps &&
            !activeCanvas?.is_section &&
            !activeCanvas?.is_template)
        "
      />
    </div>

    <template v-if="mode !== 'sharing'">
      <AddComponentModal v-if="!!addMode" :base-stacking-context="metadata.baseStackingContext" />
      <ComponentDrawerSettings
        v-else-if="!!componentSettingsMode"
        :base-stacking-context="metadata.baseStackingContext"
      />
      <CanvasDrawerTheme v-else-if="isEditingCanvasTheme" :base-stacking-context="metadata.baseStackingContext" />
      <iframe
        v-else-if="!!contentSelectorMode && useCustomContentSelector"
        ref="contentSelectorIframeRef"
        class="fixed w-full h-full border-none top-0 left-0 z-9999"
        :src="contentSelectorReplacementAppUrl ?? ''"
        title="third party content selector"
      />
      <ContentSelector
        v-else-if="!!contentSelectorMode && !useCustomContentSelector"
        :allow-multimedia-selector-app="contentSelectorMode.componentType === ComponentTypes.Multimedia"
        :allowed-asset-content-types="contentSelectorMode.allowedAssetContentTypes"
        :allowed-content-types="contentSelectorMode.allowedContentTypes"
        :allowed-ids="contentSelectorMode.allowedIds"
        :allowed-selector-types="contentSelectorMode.allowedSelectorTypes"
        :app-name="mode"
        content-type="content"
        :initial-metadata-filters="contentSelectorMode.initialMetadataFilters"
        :max-selections="contentSelectorMode.maxSelections"
        :min-selections="0"
        :on-favorites-fetch="fetchFavorites"
        :on-file-delete="deleteFile"
        :on-file-fetch="fetchFile"
        :on-file-open="
          ({ fileId, pageIndex, newTab }) => onFileOpen({ file_id: fileId, page_index: pageIndex, newTab })
        "
        :on-files-list="listFiles"
        :on-folder-fetch="fetchFolder!"
        :on-recent-files-fetch="fetchRecentFiles"
        :selections="contentSelectorMode.selections"
        @asset-deleted="deleteAsset"
        @cancel="clearContentSelectorMode()"
        @primary-action="onContentSelectorPrimaryAction"
      />
      <ComponentEdit v-else-if="!!componentEditMode" :base-stacking-context="metadata.baseStackingContext" />

      <CConfirmationModal
        v-if="isOpenPhotosCaptured"
        v-model="isOpenPhotosCaptured"
        :confirmText="t('canvasUI.canvasBuilder.notificationOnPhotosCaptured.confirm')"
        :isCancel="false"
        :title="t('canvasUI.canvasBuilder.notificationOnPhotosCaptured.title')"
        @confirm="() => (isOpenPhotosCaptured = false)"
      >
        <div class="my-6 text-size-4">
          {{ t('canvasUI.canvasBuilder.notificationOnPhotosCaptured.content') }}
        </div>
      </CConfirmationModal>
    </template>
  </div>
  <CanvasCompletionWizard v-if="isImpact && isCanvas && isCompletionWizardShowing && !isCanvasPreviewMode" />
</template>

<script setup lang="ts">
import { isEqual, isEmpty, keyBy, cloneDeep } from 'lodash-es'
import { NProgress } from 'naive-ui'
import {
  ref,
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  provide,
  watch,
  watchEffect,
  type ComputedRef,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { syncRef } from '@vueuse/core'

import { navigateToEmbeddableForPages } from '@canvas-builder/App.helpers'
import AddComponentModal from '@canvas-builder/components/container/AddComponentModal/AddComponentModal.vue'
import CanvasContent from '@canvas-builder/components/container/CanvasContent/CanvasContent.vue'
import CanvasDrawerTheme from '@canvas-builder/components/container/CanvasDrawerTheme/CanvasDrawerTheme.vue'
import ComponentDrawerSettings from '@canvas-builder/components/container/ComponentDrawerSettings/ComponentDrawerSettings.vue'
import ComponentEdit from '@canvas-builder/components/container/ComponentEdit/ComponentEdit.vue'
import ControlBar from '@canvas-builder/components/container/ControlBar/ControlBar.vue'
import Navbar from '@canvas-builder/components/container/Navbar/Navbar.vue'
import CanvasCompletionWizard from '@canvas-builder/components/container/CanvasCompletionWizard/CanvasCompletionWizard.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasNodes from '@canvas-builder/composables/useCanvasNodes'
import { cleanup as cleanupFetchedBlocks } from '@canvas-builder/composables/useCanvasBlocks'
import useCanvasHooks from '@canvas-builder/composables/useCanvasHooks'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useCanvasTheme from '@canvas-builder/composables/useCanvasTheme'
import useCanvasThemeAssets from '@canvas-builder/composables/useCanvasThemeAssets'
import useContentSelector from '@canvas-builder/composables/useContentSelector'
import useCanvasCompletionWizard from '@canvas-builder/composables/useCanvasCompletionWizard'
import useCanvasExit from '@canvas-builder/composables/useCanvasExit'
import { useInstalledEmbeddables } from '@canvas-builder/composables/useInstalledEmbeddables'
import { usePitcherEvents } from '@canvas-builder/composables/usePitcherEvents'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import {
  CanvasBuilderMode,
  ComponentTypes,
  LinkAnchorTypes,
  type CanvasBuilderProps,
  type CanvasTheme,
  type ComponentNode,
  type ContentGridProps,
  type LinkProps,
  type ComponentNodeThemeMeta,
} from '@canvas-builder/types/canvas'
import { scrollCanvasToTop, scrollToComponentById } from '@canvas-builder/util/canvas.util'
import ContentSelector from '@content-selector/App.vue'
import type {
  ContentSelectorSelectedItem,
  SelectedAsset,
  SelectedExternalLink,
  SelectedFile,
  SelectedPage,
} from '@content-selector/stores/app'
import { clearCache as clearCollectionPlayerCache } from '@lib/apps/collection-player/export'
import CConfirmationModal from '@lib/components/CConfirmationModal/CConfirmationModal.vue'
import useAppEmit from '@lib/composables/appEmit.use'
import useThemeVars from '@lib/composables/useThemeVars'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { waitForIframeInitialize } from '@lib/utils/iframeInitialize'
import type { AppFile } from '@lib/types/app'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import usePrintMode from '@canvas-builder/composables/usePrintMode'
import type { CanvasRetrieve } from '@openapi'
import useAnnotation from '@canvas-builder/composables/useAnnotation.ts'
import useControlBarCollapse from '@canvas-builder/composables/useControlBarCollapse'
import { collectAllNodesByCondition } from './util/tree'
import type { ScribbleProps } from './types/canvas'

const props = defineProps<CanvasBuilderProps>()

const emit = defineEmits<{
  toggleFullscreen: [isFullscreen: boolean, options: { skipJsApiEvent?: boolean }]
  activeNavigationItemChange?: [{ id: string; sectionId?: string; previewMode?: boolean }]
  toggleNotes: []
  cancelCanvasCompletionWizard: []
  canvasShared: [canvas: CanvasRetrieve]
  previewCanvas: [canvasId: CanvasRetrieve['id']]
}>()

useAppEmit(emit)

const { cleanup: cleanupInstalledEmbeddables } = useInstalledEmbeddables()
const themeVars = useThemeVars()
const { t } = useI18n()
const { resetControlBarsCollapse: clearControlBarsCollapse } = useControlBarCollapse()
const {
  addMode,
  canvasContent,
  componentEditMode,
  componentSettingsMode,
  componentNodesById,
  contentRef,
  isEditMode,
  displayMode,
  activeCanvas,
  mode: activeMode,
  isCanvasPreviewMode,
  isFullscreen,
  isImpact,
  isCanvas,
  isAdmin,
  sectionComponentNodesBySectionId,
  isSameAsSaved,
  activeNavigationItems,
  fileDataCache,
  initialize,
  setCanvasMetadata,
  updateNodeData,
  patchNodeById,
  updateNodeDataById,
  updateActiveCanvasProperties,
  setInCall,
  saveCanvasContent,
  setIsEditMode,
  exitFullscreen,
  navigateToHome,
  cleanup: cleanupCanvasState,
  updateMultimediaNodeWithThemeAsset,
  areThemeAssetsEnabled,
  writeablePitcherSettings,
  contentAnnotation,
  setTemporaryContextKeys,
  getSafeContext,
  watchCanvas,
  mergeIntoActiveContext,
  saveCanvasContext,
  mergeWithArrayReplacement,
} = useCanvas()

const { updateComponentsWithThemeAssets } = useCanvasNodes()

const {
  recommendedContent,
  autofillCanvasContentGridsWithMatchingProperties,
  autofillCanvasContentGridsWithAi,
  autofillSectionContentGridsWithAi,
  previewStandaloneSectionContentGridsWithMatchingProperties,
  cleanup: cleanupRecommendations,
} = useRecommendations()
const {
  applyTheme,
  isEditingCanvasTheme,
  canvasBgColor,
  cleanup: cleanupTheme,
  getDefaultTypographyConfiguration,
  loadCustomFonts,
  originalTheme,
  updateTextCSSVariables,
  updateThemeProperties,
  loadSectionThemes,
  maintainSectionTheme,
  loadSectionThemesAssets,
  clearSectionThemes,
} = useCanvasTheme()
const {
  activeSectionTheme,
  presentationProgress,
  canvasPages,
  reset: cleanupPages,
  goToPage,
  goToPreviousPage,
  goToNextPage,
  broadcastCanvasOpenedEvent,
  broadcastCanvasClosedEvent,
} = useCanvasPages()
const {
  clearContentSelectorMode,
  contentSelectorMode,
  prepareContentGridNodeData,
  prepareLinkNodeData,
  prepareMultimediaNodeData,
} = useContentSelector()
const { addManuallyChangedThemeAsset, clearManuallyChangedThemeAssets } = useCanvasThemeAssets()
const { onExitClick } = useCanvasExit()
const { isOpenPhotosCaptured, registerPhotosCapturedListener, unregisterPhotosCapturedListener } = usePitcherEvents(
  goToNextPage,
  goToPreviousPage,
  onExitClick,
)
const { resetPrintModeCounters: clearPrintModeCounters } = usePrintMode()

const presentationProgressPercentage = computed(() =>
  Math.round((presentationProgress.value[0] / presentationProgress.value[1]) * 100),
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const contentSelectorReplacementApp = inject<ComputedRef<AppFile | null>>(
  'contentSelectorReplacementApp',
  computed(() => null),
)

const contentSelectorReplacementAppUrl = computed(() => {
  if (!contentSelectorReplacementApp.value) return null
  const name = contentSelectorReplacementApp.value.app_metadata?.name
  if (doesLocalOverrideExist(name)) {
    return getLocalOverrideUrl(name)
  }
  return contentSelectorReplacementApp.value.content_url
})

const useCustomContentSelector = computed(() => {
  if (!contentSelectorReplacementApp.value) return false

  const allowedSelectorTypes = contentSelectorMode.value
    ? (contentSelectorMode.value.allowedSelectorTypes ?? ['content'])
    : ['content']

  return allowedSelectorTypes.includes('content')
})

const contentSelectorIframeRef = ref<HTMLIFrameElement | null>(null)

syncRef(pitcherSettings, writeablePitcherSettings, {
  immediate: true,
  direction: 'ltr',
})

const { isCompletionWizardEnabled, isCompletionWizardShowing, retriggerWizard, canRetriggerWizard, resetWizard } =
  useCanvasCompletionWizard()
const hasProgressBar = computed(
  () => !isEditMode.value && (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles',
)
const canvasTheme = computed(() => ({
  bgColor: isFullscreen.value ? canvasBgColor.value : themeVars.value.base,
}))
const themeBgColor = computed(() =>
  isFullscreen.value
    ? activeSectionTheme.value?.canvas_bg_color || canvasTheme.value.bgColor
    : canvasTheme.value.bgColor,
)

const shouldShowCanvasName = computed(() => {
  // Check LaunchDarkly flag first

  if (!launchDarkly.value.show_canvas_name || isFullscreen.value || !activeCanvas.value?.name) return false

  // Admin shows in both edit and view modes
  if (isAdmin.value) return true

  // Impact only shows in edit mode
  if (isImpact.value && isEditMode.value) return true

  return false
})

const shouldShowSidebars = computed(
  () => (!isFullscreen.value || pitcherSettings.value.alternative_canvas_fullscreen) && !isCanvasPreviewMode.value,
)

// provisions / init
provide('selectCollectionPlayerContent', props.selectCollectionPlayerContent)
provide('listCanvases', props.listCanvases)
provide('fetchInstanceMetadataTemplate', props.fetchInstanceMetadataTemplate)
provide('fetchCanvas', props.fetchCanvas)
provide('openExternalUrl', props.onOpenExternalUrl)
provide('canvasFileCache', fileDataCache) // Provide canvas-level file cache
provide(
  'inCall',
  computed(() => props.inCall),
)
provide(
  'call',
  computed(() => props.call),
)

initialize(props, launchDarkly.value)

// watchers
watchEffect(() => {
  if (isEditMode.value) {
    registerPhotosCapturedListener()
    props.onEditMode?.(true)
  } else {
    props.onEditMode?.(false)
    unregisterPhotosCapturedListener()
  }
})

watch(isEditMode, (newValue, oldValue) => {
  const navigationItem = activeNavigationItems.value[0]

  if (!navigationItem) {
    return
  }

  if (newValue === true && oldValue === false) {
    scrollToComponentById(navigationItem.id, navigationItem.sectionId, navigationItem.index)
  } else if (newValue === false && oldValue === true) {
    goToPage({
      componentId: navigationItem.id,
      scrollTo: 'top',
      sectionId: navigationItem.sectionId,
      sectionIndex: navigationItem.index,
      skipSetPreviousLocation: true,
    })
  }
})

watch([contentSelectorMode, useCustomContentSelector], async () => {
  if (contentSelectorMode.value && useCustomContentSelector.value) {
    await nextTick()
    waitForIframeInitialize(
      contentSelectorIframeRef.value,
      { ...contentSelectorMode.value, client: props.mode },
      'content-selector',
      (result?: { user_action: 'cancelled' | 'selected'; selectedItems: ContentSelectorSelectedItem[] }) => {
        if (result?.user_action === 'selected' && result?.selectedItems) {
          onContentSelectorPrimaryAction({ selectedItems: result.selectedItems })
        } else {
          clearContentSelectorMode()
        }
      },
    )
  }
})

watch(
  // update when active canvas changes
  () => props.activeCanvas,
  (c) => updateActiveCanvasProperties(c),
)

if (pitcherSettings.value?.enable_crm_privacy_mode) {
  watchCanvas()
}

if (
  props.mode === CanvasBuilderMode.IMPACT &&
  launchDarkly.value.allow_content_grid_autofill &&
  !props.activeCanvas.is_template
) {
  // AI autofill watcher
  watch(
    recommendedContent,
    async (newSuggestedContent, previousSuggestedContent) => {
      if (isCompletionWizardEnabled.value) {
        // don't autofill if completion wizard is enabled. wizard apps should handle it themselves.
        console.info('Wizard apps detected, skipping autofill')
        return
      }
      if (isEmpty(newSuggestedContent) || isEqual(newSuggestedContent, previousSuggestedContent)) return

      await autofillCanvasContentGridsWithAi(props.listFiles)
      await autofillSectionContentGridsWithAi(props.listFiles)
      await saveCanvasContent({ showToast: false })
    },
    { deep: true, immediate: true },
  )
  // Matching properties watcher
  watch(
    () => [props.activeCanvas.metadata, props.activeCanvas.tags],
    (newValue, previousValue) => {
      if (isCompletionWizardEnabled.value) {
        // don't autofill if completion wizard is enabled. wizard apps should handle it themselves.
        console.info('Wizard apps detected, skipping autofill')
        return
      }
      if (isEqual(newValue, previousValue)) return
      maybeAutofillCanvasContentGrids()
    },
  )
}

if (props.inCall) {
  watch(
    () => props.inCall,
    (value) => setInCall(value),
  )
}

if (props.canvasMetadata) {
  setCanvasMetadata(props.canvasMetadata.config)
}

const getUniqueIdsFromItems = (items: (SelectedFile | SelectedPage)[]) => {
  const ids = new Set<string>()

  items.forEach((item) => {
    if (item.type === 'file') {
      ids.add(item.id)
    } else if (item.type === 'page') {
      ids.add(item.file.id)
    }
  })

  return Array.from(ids)
}

function deleteAsset(id: string) {
  const nodes = collectAllNodesByCondition(
    canvasContent.value,
    (node) => node.type === ComponentTypes.Scribble && (node.data as ScribbleProps)?.asset_background?.asset_id === id,
  )
  nodes.forEach((node) => {
    const data = node.data as ScribbleProps
    updateNodeDataById(node.id, {
      ...data,
      asset_background: { ...data.asset_background, is_deleted: true },
    })
  })
}

async function onContentSelectorPrimaryAction({ selectedItems }: { selectedItems: ContentSelectorSelectedItem[] }) {
  if (!contentSelectorMode.value) return

  if (contentSelectorMode.value?.onSelected) {
    contentSelectorMode.value.onSelected(selectedItems as (SelectedFile | SelectedPage)[])
    clearContentSelectorMode()
    return
  }

  if (!contentSelectorMode.value?.id) return
  const handlers = {
    [ComponentTypes.ContentGrid]: handleContentGridSelection,
    [ComponentTypes.Multimedia]: handleMultimediaSelection,
    [ComponentTypes.Link]: handleLinkSelection,
  }

  const handler = handlers[contentSelectorMode.value.componentType as keyof typeof handlers]
  if (handler) {
    await handler(selectedItems)
  }

  clearContentSelectorMode()
}

async function handleContentGridSelection(selectedItems: ContentSelectorSelectedItem[]) {
  if (!contentSelectorMode.value || !contentSelectorMode.value?.id) return

  const items = selectedItems as (SelectedFile | SelectedPage)[]
  const filesById = await fetchFilesById(items)
  if (!filesById) return

  const data = prepareContentGridNodeData({
    selectedItems: items,
    filesById,
    node: componentNodesById.value[contentSelectorMode.value.id] as ComponentNode<ContentGridProps>,
  })

  updateNodeDataById(contentSelectorMode.value.id, data)
}

async function handleLinkSelection(selectedItems: ContentSelectorSelectedItem[]) {
  if (!contentSelectorMode.value || !contentSelectorMode.value?.id) return

  const items = selectedItems as (SelectedFile | SelectedPage)[]

  if (!items.length) {
    return
  }

  const filesById = await fetchFilesById(items)
  if (!filesById) return

  const data = prepareLinkNodeData({
    selectedItems: items,
    filesById,
    node: componentNodesById.value[contentSelectorMode.value.id] as ComponentNode<LinkProps>,
  })

  updateNodeDataById(
    contentSelectorMode.value.id,
    data?.file ? { ...data, page_index: data.file.page_index, link_type: LinkAnchorTypes.FILE } : {},
  )
}

function handleMultimediaSelection(selectedItems: ContentSelectorSelectedItem[]) {
  if (!contentSelectorMode.value || !contentSelectorMode.value?.id) return

  const items = selectedItems as (SelectedExternalLink | SelectedAsset)[]
  const nodeId = contentSelectorMode.value.id

  const result = prepareMultimediaNodeData({
    selectedItems: items,
  })

  if (!result) {
    updateNodeData({}, contentSelectorMode.value.id, contentSelectorMode.value.usedInSectionId)
    return
  }

  // Track that this multimedia component has been manually changed
  addManuallyChangedThemeAsset(nodeId)

  // First update the node with theme_meta if it exists
  if (result.theme_meta) {
    // For theme_meta, we need to replace the entire object rather than merge
    // because the properties are mutually exclusive (logo vs theme asset)
    patchNodeById(nodeId, { theme_meta: result.theme_meta as ComponentNodeThemeMeta })
  } else {
    // Remove theme_meta if it's not in the selection
    patchNodeById(nodeId, { theme_meta: undefined })
  }

  // Then update the data separately to ensure both changes take effect
  updateNodeData(result.data, contentSelectorMode.value.id, contentSelectorMode.value.usedInSectionId)
}

async function fetchFilesById(items: (SelectedFile | SelectedPage)[]) {
  try {
    const fileIds = getUniqueIdsFromItems(items)
    const files = await Promise.all(fileIds.map(props.fetchFile))
    return keyBy(files, 'id')
  } catch (e) {
    console.error('[App.vue]: failed to fetch files', e)
    return null
  }
}

function initFonts() {
  if (activeMode.value !== CanvasBuilderMode.SHARING) loadCustomFonts(props.listFiles)
}

function maybePreviewSectionContentGridRecommendations() {
  if (
    props.activeCanvas.is_section &&
    !props.activeCanvas.is_template &&
    launchDarkly.value.allow_content_grid_autofill &&
    !isCompletionWizardEnabled.value
  ) {
    previewStandaloneSectionContentGridsWithMatchingProperties({
      filesFetcher: props.listFiles,
      recommendedFilesFetcher: props.listRecommendedFiles,
    })
  }
}

async function maybeAutofillCanvasContentGrids() {
  if (
    props.mode === CanvasBuilderMode.IMPACT &&
    !props.activeCanvas.is_template &&
    !props.activeCanvas.is_section &&
    launchDarkly.value.allow_content_grid_autofill &&
    !isCompletionWizardEnabled.value
  ) {
    await autofillCanvasContentGridsWithMatchingProperties({
      filesFetcher: props.listFiles,
      recommendedFilesFetcher: props.listRecommendedFiles,
      instanceSettings: pitcherSettings.value,
    })
  }
}

async function fetchCanvasTheme() {
  if (props.activeCanvas?.template?.id) {
    // check for theme in canvas template

    // check for canvas Id in the junction table (are there any records?)
    // if so, fetch the theme from the junction table
    const template = await props.fetchCanvas(props.activeCanvas.template.id, { fields: 'theme' })
    const activeCanvasTheme = (props.activeCanvas?.theme || {}) as CanvasTheme
    const templateTheme = (template?.theme || {}) as CanvasTheme
    if (maintainSectionTheme.value) {
      // Merge template theme with canvas theme, prioritizing canvas theme attributes
      return {
        ...templateTheme,
        ...activeCanvasTheme,
      } as CanvasTheme
    } else {
      return template?.theme as CanvasTheme
    }
  }
  // check for theme in canvas
  return props.activeCanvas?.theme
}

function openOnStart() {
  if (!props.openOnStart?.component) {
    return
  }

  if (isEditMode.value) {
    scrollToComponentById(props.openOnStart?.component)
  } else {
    goToPage({
      componentId: props.openOnStart.component,
      scrollTo: 'top',
      sectionId: props.openOnStart?.section,
      skipSetPreviousLocation: true,
    })
  }
}

async function maybeUpdateTheme() {
  if (launchDarkly.value.enable_themes) {
    originalTheme.value = props.theme
    applyTheme(props.theme, props.activeCanvas.theme as Partial<CanvasTheme> | undefined)

    // First update all components with canvas theme assets if theme assets are enabled
    if (props.theme && areThemeAssetsEnabled.value) {
      updateComponentsWithThemeAssets(
        props.theme,
        componentNodesById.value as Record<string, ComponentNode>,
        updateNodeDataById,
        activeCanvas.value,
      )
    }

    if (props.activeCanvas?.sections?.length) {
      if (maintainSectionTheme.value) {
        // If maintaining section themes, apply section typography and assets
        loadSectionThemes(props.activeCanvas.sections)
        if (props.theme && areThemeAssetsEnabled.value) {
          loadSectionThemesAssets(props.activeCanvas.sections, updateMultimediaNodeWithThemeAsset)
        }
      } else {
        // If not maintaining section themes, clear section themes
        clearSectionThemes(props.activeCanvas.sections)
        // Re-apply canvas theme to all multimedia components to ensure they use canvas theme assets
        if (props.theme && areThemeAssetsEnabled.value) {
          updateComponentsWithThemeAssets(
            props.theme,
            componentNodesById.value as Record<string, ComponentNode>,
            updateNodeDataById,
            activeCanvas.value,
          )
        }
      }
    }
  } else if (launchDarkly.value.enable_canvas_tokens) {
    const theme = await fetchCanvasTheme()
    if (theme) {
      updateThemeProperties(theme)
    }
    updateTextCSSVariables(theme?.typography ? theme.typography : getDefaultTypographyConfiguration())
  }
}

contentAnnotation.value = useAnnotation()

onMounted(async () => {
  initFonts()
  maybeAutofillCanvasContentGrids()
  maybePreviewSectionContentGridRecommendations()
  openOnStart()
  maybeUpdateTheme()
  props.onEnter?.()
  broadcastCanvasOpenedEvent()

  // Expose retrigger wizard functionality on window object
  window.retriggerWizard = retriggerWizard

  // Expose canvas context helper functions on window object
  window.getCanvasContext = () => {
    if (!activeCanvas.value) return null

    return cloneDeep(activeCanvas.value?.context ?? {})
  }

  window.updateCanvasContext = async (context: Record<string, any>, options?: { showToast?: boolean }) => {
    if (!activeCanvas.value) {
      throw new Error('No active canvas')
    }

    const mergedContext = mergeWithArrayReplacement(activeCanvas.value?.context ?? {}, context)

    await saveCanvasContext(mergedContext, options)
  }
})

onBeforeUnmount(() => {
  const canvasId = activeCanvas.value?.id
  if (canvasId) {
    broadcastCanvasClosedEvent(canvasId)
  }
  exitFullscreen()
})

onUnmounted(() => {
  // delay teardown to allow onComponentExit firing
  setTimeout(() => teardown())
})

function teardown() {
  // Teardown existing state
  cleanupCanvasState()
  cleanupTheme()
  cleanupPages()
  cleanupRecommendations()
  cleanupInstalledEmbeddables()
  clearCollectionPlayerCache()
  cleanupFetchedBlocks()
  clearManuallyChangedThemeAssets()
  clearPrintModeCounters()
  clearControlBarsCollapse()

  // Clear Scribble file cache
  if (window.__scribbleFileCache) {
    window.__scribbleFileCache.clear()
  }
  if (window.__scribblePendingRequests) {
    window.__scribblePendingRequests.clear()
  }

  // Clean up window object references
  window.retriggerWizard = undefined
  window.getCanvasContext = undefined
  window.updateCanvasContext = undefined
}

defineExpose({
  deleteAsset,
  hooks: {
    onCanvasUpdated: useCanvasHooks().onCanvasUpdatedHook,
    onSectionListUpdate: useCanvasHooks().onSectionListUpdateHook,
  },
  switchToPresentation: async () => {
    setIsEditMode(false)
    await nextTick()
    scrollCanvasToTop()
  },
  setTemporaryContextKeys,
  setInMemoryAccountData: (accountData?: Record<string, any>) => {
    if (!accountData) return
    setTemporaryContextKeys('fetchedAccountData', accountData)
    mergeIntoActiveContext(accountData)
  },
  getSafeContext,
  goToPage,
  goToPreviousPage,
  goToNextPage,
  exitFullscreen,
  navigateToHome,
  navigateToEmbeddable: (appFile: AppFile) => {
    const appName = appFile.app_metadata?.name
    if (!appName) {
      console.warn('App name not found in app file metadata', appFile)
      return
    }

    if ((pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'collapsibles') {
      console.warn('navigateToEmbeddable is not supported in collapsibles mode.')
    } else {
      navigateToEmbeddableForPages({
        appName,
        canvasPages: canvasPages.value,
        componentNodesById: componentNodesById.value,
        sectionComponentNodesBySectionId: sectionComponentNodesBySectionId.value,
        isEditMode: isEditMode.value,
        canvasContentElement: contentRef.value?.$el,
      })
    }
  },
  builderHasChanges: computed(() => !isSameAsSaved.value),
  // Completion wizard functions
  retriggerWizard,
  canRetriggerWizard,
  resetWizard,
})
</script>

<style>
.c-theme-preset--h_1 {
  font-family: var(--theme-h_1-font-family);
  font-size: var(--theme-h_1-font-size);
  font-weight: var(--theme-h_1-font-weight);
  line-height: var(--theme-h_1-line-height);
  letter-spacing: var(--theme-h_1-letter-spacing);
  text-transform: var(--theme-h_1-text-transform);
  color: var(--theme-h_1-color);
}

.c-theme-preset--h_2 {
  font-family: var(--theme-h_2-font-family);
  font-size: var(--theme-h_2-font-size);
  font-weight: var(--theme-h_2-font-weight);
  line-height: var(--theme-h_2-line-height);
  letter-spacing: var(--theme-h_2-letter-spacing);
  text-transform: var(--theme-h_2-text-transform);
  color: var(--theme-h_2-color);
}

.c-theme-preset--h_3 {
  font-family: var(--theme-h_3-font-family);
  font-size: var(--theme-h_3-font-size);
  font-weight: var(--theme-h_3-font-weight);
  line-height: var(--theme-h_3-line-height);
  letter-spacing: var(--theme-h_3-letter-spacing);
  text-transform: var(--theme-h_3-text-transform);
  color: var(--theme-h_3-color);
}

.c-theme-preset--big_text,
.editor-content .c-theme-preset--big_text {
  font-family: var(--theme-big_text-font-family);
  font-size: var(--theme-big_text-font-size);
  font-weight: var(--theme-big_text-font-weight);
  line-height: var(--theme-big_text-line-height);
  letter-spacing: var(--theme-big_text-letter-spacing);
  text-transform: var(--theme-big_text-text-transform);
  color: var(--theme-big_text-color);
}

.c-theme-preset--normal_text,
.editor-content .c-theme-preset--normal_text,
.c-theme-preset--placeholder {
  font-family: var(--theme-normal_text-font-family);
  font-size: var(--theme-normal_text-font-size);
  font-weight: var(--theme-normal_text-font-weight);
  line-height: var(--theme-normal_text-line-height);
  letter-spacing: var(--theme-normal_text-letter-spacing);
  text-transform: var(--theme-normal_text-text-transform);
  color: var(--theme-normal_text-color);
}

.c-theme-preset--small_text,
.editor-content .c-theme-preset--small_text {
  font-family: var(--theme-small_text-font-family);
  font-size: var(--theme-small_text-font-size);
  font-weight: var(--theme-small_text-font-weight);
  line-height: var(--theme-small_text-line-height);
  letter-spacing: var(--theme-small_text-letter-spacing);
  text-transform: var(--theme-small_text-text-transform);
  color: var(--theme-small_text-color);
}

.c-theme-preset--h_1 span,
.c-theme-preset--h_2 span,
.c-theme-preset--h_3 span,
.c-theme-preset--big_text span,
.c-theme-preset--normal_text span,
.c-theme-preset--small_text span,
.editor-content .c-theme-preset--h_1 span,
.editor-content .c-theme-preset--h_2 span,
.editor-content .c-theme-preset--h_3 span,
.editor-content .c-theme-preset--big_text span,
.editor-content .c-theme-preset--normal_text span,
.editor-content .c-theme-preset--small_text span {
  line-height: 120%;
}

/* Section-scoped theme classes */
.canvas-section {
  /* These CSS variables will be set dynamically for each section */
  --section-h_1-font-family: var(--theme-h_1-font-family);
  --section-h_1-font-size: var(--theme-h_1-font-size);
  --section-h_1-font-weight: var(--theme-h_1-font-weight);
  --section-h_1-line-height: var(--theme-h_1-line-height);
  --section-h_1-letter-spacing: var(--theme-h_1-letter-spacing);
  --section-h_1-text-transform: var(--theme-h_1-text-transform);
  --section-h_1-color: var(--theme-h_1-color);

  --section-h_2-font-family: var(--theme-h_2-font-family);
  --section-h_2-font-size: var(--theme-h_2-font-size);
  --section-h_2-font-weight: var(--theme-h_2-font-weight);
  --section-h_2-line-height: var(--theme-h_2-line-height);
  --section-h_2-letter-spacing: var(--theme-h_2-letter-spacing);
  --section-h_2-text-transform: var(--theme-h_2-text-transform);
  --section-h_2-color: var(--theme-h_2-color);

  --section-h_3-font-family: var(--theme-h_3-font-family);
  --section-h_3-font-size: var(--theme-h_3-font-size);
  --section-h_3-font-weight: var(--theme-h_3-font-weight);
  --section-h_3-line-height: var(--theme-h_3-line-height);
  --section-h_3-letter-spacing: var(--theme-h_3-letter-spacing);
  --section-h_3-text-transform: var(--theme-h_3-text-transform);
  --section-h_3-color: var(--theme-h_3-color);

  --section-big_text-font-family: var(--theme-big_text-font-family);
  --section-big_text-font-size: var(--theme-big_text-font-size);
  --section-big_text-font-weight: var(--theme-big_text-font-weight);
  --section-big_text-line-height: var(--theme-big_text-line-height);
  --section-big_text-letter-spacing: var(--theme-big_text-letter-spacing);
  --section-big_text-text-transform: var(--theme-big_text-text-transform);
  --section-big_text-color: var(--theme-big_text-color);

  --section-normal_text-font-family: var(--theme-normal_text-font-family);
  --section-normal_text-font-size: var(--theme-normal_text-font-size);
  --section-normal_text-font-weight: var(--theme-normal_text-font-weight);
  --section-normal_text-line-height: var(--theme-normal_text-line-height);
  --section-normal_text-letter-spacing: var(--theme-normal_text-letter-spacing);
  --section-normal_text-text-transform: var(--theme-normal_text-text-transform);
  --section-normal_text-color: var(--theme-normal_text-color);

  --section-small_text-font-family: var(--theme-small_text-font-family);
  --section-small_text-font-size: var(--theme-small_text-font-size);
  --section-small_text-font-weight: var(--theme-small_text-font-weight);
  --section-small_text-line-height: var(--theme-small_text-line-height);
  --section-small_text-letter-spacing: var(--theme-small_text-letter-spacing);
  --section-small_text-text-transform: var(--theme-small_text-text-transform);
  --section-small_text-color: var(--theme-small_text-color);
}

.canvas-section .c-theme-preset--h_1 {
  font-family: var(--section-h_1-font-family);
  font-size: var(--section-h_1-font-size);
  font-weight: var(--section-h_1-font-weight);
  line-height: var(--section-h_1-line-height);
  letter-spacing: var(--section-h_1-letter-spacing);
  text-transform: var(--section-h_1-text-transform);
  color: var(--section-h_1-color);
}

.canvas-section .c-theme-preset--h_2 {
  font-family: var(--section-h_2-font-family);
  font-size: var(--section-h_2-font-size);
  font-weight: var(--section-h_2-font-weight);
  line-height: var(--section-h_2-line-height);
  letter-spacing: var(--section-h_2-letter-spacing);
  text-transform: var(--section-h_2-text-transform);
  color: var(--section-h_2-color);
}

.canvas-section .c-theme-preset--h_3 {
  font-family: var(--section-h_3-font-family);
  font-size: var(--section-h_3-font-size);
  font-weight: var(--section-h_3-font-weight);
  line-height: var(--section-h_3-line-height);
  letter-spacing: var(--section-h_3-letter-spacing);
  text-transform: var(--section-h_3-text-transform);
  color: var(--section-h_3-color);
}

.canvas-section .c-theme-preset--big_text,
.canvas-section .editor-content .c-theme-preset--big_text {
  font-family: var(--section-big_text-font-family);
  font-size: var(--section-big_text-font-size);
  font-weight: var(--section-big_text-font-weight);
  line-height: var(--section-big_text-line-height);
  letter-spacing: var(--section-big_text-letter-spacing);
  text-transform: var(--section-big_text-text-transform);
  color: var(--section-big_text-color);
}

.canvas-section .c-theme-preset--normal_text,
.canvas-section .editor-content .c-theme-preset--normal_text,
.canvas-section .c-theme-preset--placeholder {
  font-family: var(--section-normal_text-font-family);
  font-size: var(--section-normal_text-font-size);
  font-weight: var(--section-normal_text-font-weight);
  line-height: var(--section-normal_text-line-height);
  letter-spacing: var(--section-normal_text-letter-spacing);
  text-transform: var(--section-normal_text-text-transform);
  color: var(--section-normal_text-color);
}

.canvas-section .c-theme-preset--small_text,
.canvas-section .editor-content .c-theme-preset--small_text {
  font-family: var(--section-small_text-font-family);
  font-size: var(--section-small_text-font-size);
  font-weight: var(--section-small_text-font-weight);
  line-height: var(--section-small_text-line-height);
  letter-spacing: var(--section-small_text-letter-spacing);
  text-transform: var(--section-small_text-text-transform);
  color: var(--section-small_text-color);
}

.canvas-section .c-theme-preset--h_1 span,
.canvas-section .c-theme-preset--h_2 span,
.canvas-section .c-theme-preset--h_3 span,
.canvas-section .c-theme-preset--big_text span,
.canvas-section .c-theme-preset--normal_text span,
.canvas-section .c-theme-preset--small_text span,
.canvas-section .editor-content .c-theme-preset--h_1 span,
.canvas-section .editor-content .c-theme-preset--h_2 span,
.canvas-section .editor-content .c-theme-preset--h_3 span,
.canvas-section .editor-content .c-theme-preset--big_text span,
.canvas-section .editor-content .c-theme-preset--normal_text span,
.canvas-section .editor-content .c-theme-preset--small_text span {
  line-height: 120%;
}

.canvas-builder-app--sharing {
  .hide-in-sharing {
    display: none;
  }
}
.canvas-builder-app--print {
  .hide-in-print {
    display: none;
  }
}
</style>
